import * as RadixSlot from '@radix-ui/react-slot';
import cn from 'classnames';

import { Checkbox } from '../../Checkbox';
import { CardContent, type CardContentProps } from '../shared/CardContent';
import { CardMedia, type CardMediaProps } from '../shared/CardMedia';
import {
  CardMediaAdornment,
  type CardMediaAdornmentProps,
} from '../shared/MediaAdornment';
import basicCardStyles from '../shared/styles.module.css';

import styles from './ThumbnailCard.module.css';
import type { ThumbnailCardProps } from './types';
import { useKeyboardInteraction } from './useKeyboardInteraction';

type ThumbnailCardComponent = React.FC<ThumbnailCardProps> & {
  Media: React.FC<CardMediaProps>;
  MediaAdornment: React.FC<CardMediaAdornmentProps>;
  Content: React.FC<CardContentProps>;
};

/**
 * ThumbnailCard is a component that displays a thumbnail card with a media section and an optional content section.
 * It is used to display a single item in a carousel/grid/masonry layout.
 */
export const ThumbnailCard: ThumbnailCardComponent = ({
  onSelectionChange,
  selected,
  dataTestId,
  className,
  asChild,
  children,
  size = 'm',
  borderless,
  role,
  ...rest
}) => {
  const handleKeyDown = useKeyboardInteraction({
    onSelectionChange,
    selected,
  });

  // If some other component is inside the Card, we need to use the `asChild` prop to pass it as a child.
  const Component = asChild ? RadixSlot.Root : 'div';
  const content = asChild ? (
    <RadixSlot.Slottable>{children}</RadixSlot.Slottable>
  ) : (
    children
  );

  const cardClassName = cn(
    className,
    basicCardStyles.card,
    styles['thumbnail-card'],
    styles['thumbnail-card-media-styling'],
    styles[`thumbnail-card-size-${size}`],
    borderless && styles['thumbnail-card-borderless']
  );

  return (
    <Component
      aria-selected={selected} // Convey the active/selected state in case not disabled
      className={cardClassName}
      data-testid={dataTestId}
      role={role}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {content}
      {onSelectionChange && (
        <CardMediaAdornment
          data-selected={selected}
          position="top-left"
          showType={selected ? 'always' : 'on-hover'}
        >
          <Checkbox
            ariaLabel="Select Item"
            checked={selected}
            dataTestId={`${dataTestId}--select-checkbox`}
            size="m"
            //This is needed to prevent the checkbox from being focused: it's either handled by the `Space` key
            //or by the generic pointer interaction.
            tabIndex={-1}
            onCheckedChange={(checked) => onSelectionChange(checked === true)}
          />
        </CardMediaAdornment>
      )}
    </Component>
  );
};

ThumbnailCard.Media = CardMedia;
ThumbnailCard.Content = CardContent;
ThumbnailCard.MediaAdornment = CardMediaAdornment;
